import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { TResponse, TResponseWithQData } from "../../../types/common";
import axios from "../../../lib/axios";

export type TenderStatus = "preparing" | "submitted" | "under_review" | "won" | "lost" | "withdrawn" | "no_bid";

export interface ITender {
  id: number;
  company_id: number;
  client_id: number | null;
  client_name: string | null;
  tender_number: string | null;
  title: string;
  description: string | null;
  status: TenderStatus;
  tender_value: number | null;
  submission_deadline: string | null;
  decision_date: string | null;
  issuing_authority: string | null;
  contract_type: string | null;
  notes: string | null;
  created_at: string;
}

const listTenders = (params: any) =>
  axios.get(API_ENDPOINTS.TENDER_LIST, { params }).then((r) => r.data as TResponseWithQData<ITender[]>);

const createTender = (data: any) =>
  axios.post(API_ENDPOINTS.TENDER_CREATE, data).then((r) => r.data as TResponse<ITender>);

const updateTender = (data: any) =>
  axios.post(API_ENDPOINTS.TENDER_UPDATE, data).then((r) => r.data as TResponse<ITender>);

const deleteTender = (id: number) =>
  axios.post(API_ENDPOINTS.TENDER_DELETE, { id }).then((r) => r.data as TResponse<null>);

export const useTenderList = (initialParams: any) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery<TResponseWithQData<ITender[]>, Error>({
    queryKey: [API_ENDPOINTS.TENDER_LIST, params],
    queryFn: () => listTenders(params),
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useTenderCreate = (options?: { onSuccess?: (d: ITender) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation<TResponse<ITender>, Error, any>({
    mutationFn: createTender,
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.TENDER_LIST] }); options?.onSuccess?.(res.data as ITender); },
    onError: options?.onError,
  });
};

export const useTenderUpdate = (options?: { onSuccess?: (d: ITender) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation<TResponse<ITender>, Error, any>({
    mutationFn: updateTender,
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.TENDER_LIST] }); options?.onSuccess?.(res.data as ITender); },
    onError: options?.onError,
  });
};

export const useTenderDelete = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation<TResponse<null>, Error, number>({
    mutationFn: deleteTender,
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.TENDER_LIST] }); options?.onSuccess?.(); },
    onError: options?.onError,
  });
};
